import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import moment from 'moment';
import { FromDatePipe } from '@netfoundry-ui/ui/pipes';
import { CsvDownloadService, TableFilterService } from '@netfoundry-ui/feature/shared-services';
import { Subscription } from 'rxjs';
import { EVENT_FIELDS, EventFields, NetworkV2 } from '@netfoundry-ui/shared/model';
import { ApiService, FeatureService } from '@netfoundry-ui/shared/services';
import { TEMPLATE_SEARCH_SERVICE, TemplateSearchService } from '@netfoundry-ui/shared/apiv2';
import { debounceTime } from 'rxjs/operators';
import semver from 'semver';

@Component({
    selector: 'app-metrics-tab',
    templateUrl: './metrics-tab.component.html',
    styleUrls: ['./metrics-tab.component.scss'],
    providers: [FromDatePipe, CsvDownloadService],
    standalone: false,
})
export class MetricsTabComponent implements OnInit, OnDestroy {
    @Input() data: any;

    setDateFilterEvent: EventEmitter<boolean> = new EventEmitter();
    setNewEndTime: EventEmitter<string> = new EventEmitter();
    setNewStartTime: EventEmitter<string> = new EventEmitter();

    dateFilter = '24h';
    endTime: number = Date.now();
    startTime: number = this.endTime - 24 * 60 * 60 * 1000;

    currentNetwork = new NetworkV2();

    networkId;
    networkGroupId;
    resourceType;
    model;
    filterType: string;
    filterValue: string;

    rangeDates: Date[];
    startDate;
    endDate;
    minimumDate;

    serviceItems;
    endpointItems;
    label = 'Edge Routers';
    labelEndpoint = this.featureService?.disableNewNavigation ? 'Endpoints' : 'Identities';
    usageHostingLabel = this.featureService?.disableNewNavigation ? 'Hosting Endpoints' : 'Hosting Identities';

    aggregateField;
    endpointTableFilterValue: string;
    serviceTableFilterValue: string;
    topUsageGridStyleClass = 'halfGrid';
    serviceTableLocked = false;
    hostingEndpointData = false;
    productVersion;

    private subscription = new Subscription();

    filterTypes = {
        endpoint: this.eventFields.identityId,
        service: this.eventFields.serviceId,
        'edge-router': this.eventFields.edgeRouterId,
    };

    constructor(
        private fromDate: FromDatePipe,
        private csvDownloadService: CsvDownloadService,
        public filterService: TableFilterService,
        public apiService: ApiService,
        public featureService: FeatureService,
        @Inject(TEMPLATE_SEARCH_SERVICE) public templateService: TemplateSearchService,
        @Inject(EVENT_FIELDS) private eventFields: EventFields,
    ) {
        this.minimumDate = new Date();
        this.minimumDate.setDate(this.minimumDate.getDate() - 30);
    }

    ngOnInit() {
        this.data = this.data || {};
        this.networkGroupId = this.data.networkGroupId;
        this.networkId = this.data.networkId;
        this.resourceType = this.data.resourceType;
        this.topUsageGridStyleClass =
            this.resourceType === 'network'
                ? 'thirdGrid'
                : this.resourceType !== 'endpoint'
                  ? 'thirdGrid'
                  : 'halfGrid';
        this.model = this.data.model;
        this.filterType = this.data.filterType || this.filterTypes[this.resourceType];
        this.filterValue = this.data.resourceValue || this.model?.id;

        this.subscription.add(
            this.apiService.currentNetwork.pipe(debounceTime(500)).subscribe((network) => {
                this.currentNetwork = network;
                this.getProductVersion();
            }),
        );
        this.filterService.getDateFilter();
        this.closeCalendar();

        // The charts render inside lazy mat-tab content and are frequently built at zero
        // width, leaving them blank. Highcharts auto-reflows on window resize, so nudge
        // them as the layout/data settle after first load.
        this.scheduleChartReflow();
    }

    // Highcharts listens for window resize and recomputes each chart's size from its
    // container, so firing it forces the zero-width tab charts to draw.
    reflowCharts() {
        setTimeout(() => window.dispatchEvent(new Event('resize')));
    }

    private scheduleChartReflow() {
        [800, 2000, 4000].forEach((delay) => setTimeout(() => this.reflowCharts(), delay));
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    public setStartTime(value) {
        this.startTime = this.fromDate.transform(value);
        this.endTime = Date.now();
        if (value === '1m' || value === '6m' || value === '12m') {
            this.endTime = moment(this.endTime).endOf('month').valueOf();
        }
        this.dateFilter = value;
    }

    updateStart(value) {
        this.dateFilter = value;
        this.setStartTime(this.dateFilter);
        this.setEndTime();
        this.setDateFilterEvent.emit();
        this.rangeDates = null;
        this.closeCalendar();
        this.scheduleChartReflow();
    }

    getProductVersion() {
        this.productVersion = this.currentNetwork.productVersion;
        if (semver.gte(this.productVersion, '7.3.79')) {
            this.hostingEndpointData = true;
            this.label = this.featureService?.disableNewNavigation ? 'Hosting Endpoints' : 'Hosting Identities';
            this.labelEndpoint = this.featureService?.disableNewNavigation ? 'Dialing Endpoints' : 'Dialing Identities';
            this.aggregateField = this.templateService.host_aggregate_field;
        } else {
            this.aggregateField = this.templateService.edge_router_aggregate_field;
        }
    }

    closeCalendar() {
        if (this.rangeDates?.length >= 2 && this.rangeDates[0] != null && this.rangeDates[1] != null) {
            this.filterService.setDateRange(moment.utc(this.rangeDates[0]), moment.utc(this.rangeDates[1]));
        }
    }

    public setEndTime() {
        this.endTime = Date.now();
    }

    receivedServiceItems(items) {
        this.serviceItems = items;
    }

    receivedEndpointItems(items) {
        this.endpointItems = items;
    }

    downloadServiceCsv() {
        this.downloadCsv('serviceUsageTotals', this.serviceItems);
    }

    downloadEndpointCsv() {
        this.downloadCsv('endpointUsageTotals', this.endpointItems);
    }

    downloadCsv(filename, items) {
        const csvItems = [];
        for (const item of items) {
            csvItems.push(item);
        }
        this.csvDownloadService.download(filename, csvItems, ['name', 'usage']);
    }

    setFilter(_field: string, _value: any) {
        // placeholder — hosting endpoint filter not wired in this context
    }
}
